import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";

import api from "../api/axios"; // NEW

const AuthContext = createContext(null);

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkSession = async () => {
        // 🔒 BLOCK RE-LOGIN AFTER LOGOUT
        if (localStorage.getItem("loggedOut") === "true") {
            setLoading(false);
            return;
        }

        try {
            // 1. Check for real authenticated session FIRST
            const { data } = await api.get(`/auth/me?t=${Date.now()}`);
            
            // If real user found, clear guest state
            localStorage.removeItem("ayi_guest_session");
            setUser(data);
            setLoading(false);
            return;
        } catch (err) {
            // No real session found, proceed to guest check
            console.log("No real session, checking for guest resonance...");
        }

        // 2. ONLY check for Guest session if real session fails
        const guestSession = localStorage.getItem("ayi_guest_session");
        if (guestSession) {
            try {
                const { role } = JSON.parse(guestSession);
                setUser({ 
                    id: "guest", 
                    role: role, 
                    name: "O' Traveller", 
                    isGuest: true 
                });
            } catch (e) {
                localStorage.removeItem("ayi_guest_session");
            }
        } else {
            setUser(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        checkSession();
    }, []);

    const login = async (data) => {
        if (data.user) {
            localStorage.setItem("user", JSON.stringify(data.user));
            localStorage.removeItem("loggedOut");
            localStorage.removeItem("ayi_guest_session"); // Clear guest state on real login
            setUser(data.user);
        }
    };

    const logout = async () => {
        // 1. Optimistic UI reset
        setUser(null);
        
        // 2. Clear sensitive caches IMMEDIATELY
        const keysToClear = [
            "user", 
            "chatListCache", 
            "chatMessages", 
            "chatArchives", 
            "chatFavorites",
            "ayi_guest_session" // Clear guest state too
        ];
        keysToClear.forEach(key => localStorage.removeItem(key));
        
        // 🔒 2.5 SET GATE TO PREVENT AUTO-RE-LOGIN ON NEXT LOAD
        localStorage.setItem("loggedOut", "true");

        try {
            // 3. Inform backend to clear cookies (Mirrored + Local)
            await api.post("/auth/logout");
        } catch (e) {
            console.error("Logout API call failed, but local state was cleared.", e);
        }
        
        // 🚀 FORCE RELOAD TO CLEAR MEMORY AND COOKIE CACHES DEFINITIVELY
        window.location.href = "/";
    };

    const enterAsGuest = (role) => {
        localStorage.setItem("ayi_guest_session", JSON.stringify({ role, createdAt: Date.now() }));
        localStorage.removeItem("loggedOut");
        setUser({ id: "guest", role: role, name: "O' Traveller", isGuest: true });
    };

    const breakFree = () => {
        localStorage.removeItem("ayi_guest_session");
        setUser(null);
        window.location.href = "/signup";
    };

    const updateUser = (data) => setUser(prev => prev ? { ...prev, ...data } : null);

    const value = {
        user,
        loading,
        login,
        logout,
        checkSession,
        updateUser,
        enterAsGuest,
        breakFree
    };

    return (
        <AuthContext.Provider value={value}>
            {loading ? (
                <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 transition-colors">
                    <div className="w-16 h-16 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin mb-4"></div>
                    <p className="text-gray-500 font-medium animate-pulse">Centering...</p>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
}
